import { useMemo } from 'react';
import { useEntrance } from '@/components/entrance-tracker/context/entrance-context';
import { ALL_VANILLA_ENTRANCES } from '@/components/entrance-tracker/data/vanilla-entrances';

/**
 * Maps medallion/stone item ids (both legacy ootr tracker and modern Tailwind
 * tracker) to the reward id used in the entrance tracker's `zoneRewards`.
 */
export const MEDALLION_ID_TO_REWARD_ID: Record<string, string> = {
    // Legacy ootr tracker ids
    'Light Medallion': 'light-medallion',
    'Forest Medallion': 'forest-medallion',
    'Fire Medallion': 'fire-medallion',
    'Water Medallion': 'water-medallion',
    'Shadow Medallion': 'shadow-medallion',
    'Spirit Medallion': 'spirit-medallion',
    'Kokiri Emerald': 'kokiri-emerald',
    'Goron Ruby': 'goron-ruby',
    'Zora Sapphire': 'zora-sapphire',
    // Modern tracker ids (item-grid.tsx)
    'Oot_LightMedallion': 'light-medallion',
    'Oot_ForestMedallion': 'forest-medallion',
    'Oot_FireMedallion': 'fire-medallion',
    'Oot_WaterMedallion': 'water-medallion',
    'Oot_ShadowMedallion': 'shadow-medallion',
    'Oot_SpiritMedallion': 'spirit-medallion',
    'Oot_KokiriEmerald': 'kokiri-emerald',
    'Oot_GoronRuby': 'goron-ruby',
    'Oot_ZoraSapphire': 'zora-sapphire',
};

export interface WarpSongEntry {
    song: string;
    entranceId: string;
}

export const WARP_SONG_ENTRANCE_IDS: WarpSongEntry[] = [
    { song: 'Minuet of Forest', entranceId: 'Warps::Minuet of Forest' },
    { song: 'Bolero of Fire', entranceId: 'Warps::Bolero of Fire' },
    { song: 'Serenade of Water', entranceId: 'Warps::Serenade of Water' },
    { song: 'Nocturne of Shadow', entranceId: 'Warps::Nocturne of Shadow' },
    { song: 'Requiem of Spirit', entranceId: 'Warps::Requiem of Spirit' },
    { song: 'Prelude of Light', entranceId: 'Warps::Prelude of Light' },
];

/**
 * Reverse index: vanillaTo string -> source entrance (zone + label).
 * Used to resolve a warp destination to the zone where Link physically lands.
 */
const VANILLA_TO_INDEX = (() => {
    const map = new Map<string, { zone: string; label: string }>();
    for (const ent of ALL_VANILLA_ENTRANCES) {
        // Skip warps/spawns/owls — they're "sources" of teleport, not arrival entrances
        if (ent.category === 'warp' || ent.category === 'spawn' || ent.category === 'owl') continue;
        if (!map.has(ent.vanillaTo)) {
            map.set(ent.vanillaTo, { zone: ent.zone, label: ent.label });
        }
    }
    return map;
})();

/**
 * Resolves a stored warp destination (a `vanillaTo` string) into a
 * human-friendly arrival zone. When the destination matches a known entrance,
 * returns the entrance's source zone (where Link actually stands after the
 * warp). Falls back to the raw destination otherwise.
 */
export function resolveWarpArrivalZone(destination: string | null | undefined): string | null {
    if (!destination) return null;
    const entry = VANILLA_TO_INDEX.get(destination);
    return entry ? entry.zone : destination;
}

/**
 * Returns the zone where the given reward has been assigned in the entrance
 * tracker, or null if unassigned. Reverse lookup of `zoneRewards`.
 */
export function useDerivedRewardZone(rewardId: string | undefined): string | null {
    const { state } = useEntrance();
    return useMemo(() => {
        if (!rewardId) return null;
        for (const [zone, rewards] of Object.entries(state.zoneRewards)) {
            if (rewards.includes(rewardId)) return zone;
        }
        return null;
    }, [state.zoneRewards, rewardId]);
}
